/**
 * DEMO · Idea 1 — Presupuesto pre-armado en un clic.
 *
 * Real (del modelo): los TIPOS del modelo (inventario de ci) y la cantidad de cada uno (medición de ci).
 * De ejemplo: la barra de avance (simulada), el concepto de cada tipo, su precio y su semáforo.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, ProgressBar } from '@fluentui/react-components';
import { Sparkle20Regular } from '@fluentui/react-icons';
import { DemoVisor3D, type Pintura, type VisorDemoHandle } from '../../components/demos/DemoVisor3D.js';
import { DemoMarco, NotaEjemplo, Panel, estiloTabla } from '../../components/demos/DemoMarco.js';
import { CONFIANZA_LABEL, CONFIANZA_ORDEN, EncabezadoConfianza, PastillaConfianza, type ConfKind } from '../../components/demos/ConfianzaCotizacion.js';
import type { ElementoDemo, ModeloDemo } from '../../components/demos/modeloDemo.js';
import {
  HP_EJEMPLO,
  cant,
  cantidadDe,
  cantidadEstimada,
  coloresDemo,
  conceptoDe,
  crearContexto,
  nombreClase,
  pesos,
  type ConceptoEjemplo,
} from '../../components/demos/logicaDemo.js';
import datos from '../../components/demos/datos/presupuesto-en-un-clic.json';
import { LoQueFalta } from '../../components/demos/LoQueFalta.js';
import catalogo from '../../components/demos/datos/catalogo.json';

const FICHA = catalogo.demos.find((d) => d.id === 'presupuesto-en-un-clic')!;

interface Renglon {
  clave: string;
  tipos: string[];
  clase: string;
  concepto: ConceptoEjemplo | null;
  els: ElementoDemo[];
  cantidad: number;
  importe: number;
}

export function armarRenglones(modelo: ModeloDemo): Renglon[] {
  const ctx = crearContexto(modelo.elementos);
  const m = new Map<string, Renglon>();
  for (const e of modelo.elementos) {
    const c = conceptoDe(e, ctx);
    // Un renglón por concepto y clase; los tipos del modelo que caen ahí se listan en el renglón.
    const k = `${c?.id ?? '-'}|${e.clase}`;
    const r = m.get(k) ?? m.set(k, { clave: k, tipos: [], clase: e.clase, concepto: c, els: [], cantidad: 0, importe: 0 }).get(k)!;
    r.els.push(e);
    if (!r.tipos.includes(e.tipo)) r.tipos.push(e.tipo);
  }
  for (const r of m.values()) {
    r.cantidad = r.concepto ? r.els.reduce((s, e) => s + cantidadDe(e, r.concepto!.magnitud), 0) : r.els.length;
    r.importe = r.concepto ? r.cantidad * r.concepto.pu : 0;
  }
  return [...m.values()];
}

export function DemoPresupuestoUnClic() {
  const visor = useRef<VisorDemoHandle>(null);
  const [modelo, setModelo] = useState<ModeloDemo | null>(null);
  const [fase, setFase] = useState<'inicio' | 'armando' | 'listo'>('inicio');
  const [paso, setPaso] = useState(0);
  const [foco, setFoco] = useState<string | null>(null);
  const colores = useMemo(() => coloresDemo(), []);

  useEffect(() => {
    if (fase !== 'armando') return;
    if (paso >= datos.pasos.length) {
      setFase('listo');
      return;
    }
    const t = setTimeout(() => setPaso((p) => p + 1), datos.pasos[paso]!.ms);
    return () => clearTimeout(t);
  }, [fase, paso]);

  const renglones = useMemo(() => (modelo ? armarRenglones(modelo) : []), [modelo]);
  const total = renglones.reduce((s, r) => s + r.importe, 0);
  const porPartida = HP_EJEMPLO.partidas
    .map((p) => ({ p, rs: renglones.filter((r) => r.concepto?.partida === p.id) }))
    .filter((x) => x.rs.length);
  const sinConcepto = renglones.filter((r) => !r.concepto);
  const conteo: Record<ConfKind, number> = { alta: 0, media: 0, sin_equivalente: 0, sintetica: 0 };
  for (const r of renglones) conteo[r.concepto?.confianza ?? 'sin_equivalente']++;

  const focoSet = useMemo(() => new Set(renglones.find((r) => r.clave === foco)?.els.map((e) => e.clave) ?? []), [renglones, foco]);
  const pintar = useMemo(
    () =>
      (el: ElementoDemo | null, clave: string): Pintura => {
        if (!el) return { visible: false };
        if (!foco) return {};
        return focoSet.has(clave) ? { color: colores.acento, opacidad: 1 } : { color: colores.neutro, opacidad: 0.1 };
      },
    [foco, focoSet, colores],
  );

  const elegir = (r: Renglon) => {
    const nuevo = foco === r.clave ? null : r.clave;
    setFoco(nuevo);
    visor.current?.encuadrar(nuevo ? r.els.map((e) => e.clave) : undefined);
  };

  const fila = (r: Renglon) => (
    <tr
      key={r.clave}
      onClick={() => elegir(r)}
      style={{ cursor: 'pointer', background: foco === r.clave ? 'var(--accent-weak)' : undefined }}
    >
      <td style={estiloTabla.td}>
        {r.concepto ? r.concepto.descripcion : <span style={{ color: 'var(--bad)' }}>Sin concepto: elígelo tú</span>}
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
          {nombreClase(r.clase)} · {r.tipos.length === 1 ? `tipo del modelo «${r.tipos[0]}»` : `${r.tipos.length} tipos del modelo («${r.tipos.slice(0, 2).join('», «')}»${r.tipos.length > 2 ? '…' : ''})`} ·{' '}
          {r.els.length} {r.els.length === 1 ? 'pieza' : 'piezas'}
          {r.concepto && r.els.some((e) => cantidadEstimada(e, r.concepto!.magnitud)) ? ' · cantidad medida del dibujo' : ''}
        </div>
      </td>
      <td style={estiloTabla.num}>{r.concepto ? `${cant(r.cantidad, r.concepto.magnitud)} ${r.concepto.unidad}` : '—'}</td>
      <td style={estiloTabla.num}>{r.concepto ? pesos(r.concepto.pu) : '—'}</td>
      <td style={estiloTabla.num}>{r.concepto ? pesos(r.importe) : '—'}</td>
      <td style={estiloTabla.td}>
        <PastillaConfianza k={r.concepto?.confianza ?? 'sin_equivalente'} />
      </td>
    </tr>
  );

  return (
    <DemoMarco titulo={FICHA.nombre} dolor={FICHA.dolor}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.25fr)', gap: 14 }}>
        <DemoVisor3D
          ref={visor}
          fuentes={[{ clave: 'a', url: '/demos/fzk-haus-a.ifc' }]}
          onListo={(m) => setModelo(m.a ?? null)}
          pintar={pintar}
          alto={600}
        />
        <Panel titulo="Hoja de presupuesto">
          {fase === 'inicio' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start', padding: '12px 0' }}>
              <div style={{ color: 'var(--text-secondary)' }}>
                Ya subiste el modelo. En lugar de asignar tipo por tipo, deja que se arme el borrador completo y tú solo revisas lo que no tiene confianza alta.
              </div>
              <Button
                appearance="primary"
                icon={<Sparkle20Regular />}
                disabled={!modelo}
                onClick={() => {
                  setPaso(0);
                  setFase('armando');
                }}
              >
                {modelo ? 'Armar el presupuesto' : 'Abriendo el modelo…'}
              </Button>
            </div>
          )}
          {fase === 'armando' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 0' }}>
              <ProgressBar value={paso / datos.pasos.length} thickness="large" />
              <div style={{ color: 'var(--text-secondary)' }}>{datos.pasos[Math.min(paso, datos.pasos.length - 1)]!.texto}…</div>
            </div>
          )}
          {fase === 'listo' && (
            <>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <span style={{ fontSize: 'var(--fs-600)', fontWeight: 700 }}>{pesos(total)}</span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {renglones.length} renglones ·{' '}
                  {CONFIANZA_ORDEN.filter((k) => conteo[k] > 0)
                    .map((k) => `${conteo[k]} ${CONFIANZA_LABEL[k].toLowerCase()}`)
                    .join(' · ')}
                </span>
              </div>
              <table style={estiloTabla.tabla}>
                <thead>
                  <tr>
                    <th style={estiloTabla.th}>Concepto</th>
                    <th style={{ ...estiloTabla.th, textAlign: 'right' }}>Cantidad</th>
                    <th style={{ ...estiloTabla.th, textAlign: 'right' }}>P. unitario</th>
                    <th style={{ ...estiloTabla.th, textAlign: 'right' }}>Importe</th>
                    <th style={estiloTabla.th}>
                      <EncabezadoConfianza />
                    </th>
                  </tr>
                </thead>
                {porPartida.map(({ p, rs }) => (
                  <tbody key={p.id}>
                    <tr>
                      <td colSpan={3} style={{ ...estiloTabla.td, fontWeight: 700, background: 'var(--bg-raised)' }}>
                        {p.nombre}
                      </td>
                      <td style={{ ...estiloTabla.num, fontWeight: 700, background: 'var(--bg-raised)' }}>
                        {pesos(rs.reduce((s, r) => s + r.importe, 0))}
                      </td>
                      <td style={{ ...estiloTabla.td, background: 'var(--bg-raised)' }} />
                    </tr>
                    {rs.map(fila)}
                  </tbody>
                ))}
                {sinConcepto.length > 0 && (
                  <tbody>
                    <tr>
                      <td colSpan={5} style={{ ...estiloTabla.td, fontWeight: 700, background: 'var(--bg-raised)' }}>
                        Sin concepto todavía
                      </td>
                    </tr>
                    {sinConcepto.map(fila)}
                  </tbody>
                )}
              </table>
              <NotaEjemplo>Clic en un renglón para ver en el 3D qué se cotizó. {datos.aviso}</NotaEjemplo>
            </>
          )}
        </Panel>
      </div>
      {fase === 'listo' && (
        <LoQueFalta modelo={modelo} conceptosEnHoja={renglones.flatMap((r) => (r.concepto ? [r.concepto.id] : []))} />
      )}
    </DemoMarco>
  );
}
